import * as THREE from "three";
import type { ToothDataset, ToothPlacementContext } from "./ToothDataset";
import {
  getToothMeshPath,
  applyRealisticToothTransform,
  getToothAnchorHint,
} from "./ToothLibrary";
import { loadToothMesh } from "./ToothMeshFactory";

function scaleVisualToSourceHeight(
  visualRoot: THREE.Object3D,
  sourceMesh: THREE.Mesh
): void {
  const sourceBox = new THREE.Box3().setFromObject(sourceMesh);
  const sourceSize = new THREE.Vector3();
  sourceBox.getSize(sourceSize);

  const visualBox = new THREE.Box3().setFromObject(visualRoot);
  const visualSize = new THREE.Vector3();
  visualBox.getSize(visualSize);

  if (visualSize.x <= 0 || visualSize.y <= 0 || visualSize.z <= 0) {
    return;
  }

  const scaleFromHeight = sourceSize.y / visualSize.y;
  const visualBoost = 1.15;
  const uniformScale = scaleFromHeight * visualBoost;

  visualRoot.scale.multiplyScalar(uniformScale);
}

function computePointFromBoxRatio(
  object: THREE.Object3D,
  ratio: [number, number, number]
): THREE.Vector3 {
  const box = new THREE.Box3().setFromObject(object);

  if (box.isEmpty()) {
    return new THREE.Vector3(0, 0, 0);
  }

  const size = new THREE.Vector3();
  box.getSize(size);

  return new THREE.Vector3(
    box.min.x + size.x * ratio[0],
    box.min.y + size.y * ratio[1],
    box.min.z + size.z * ratio[2]
  );
}

function applyDefaultRotationDeg(
  object: THREE.Object3D,
  rotationDeg?: [number, number, number]
): void {
  if (!rotationDeg) {
    return;
  }

  object.rotation.x += THREE.MathUtils.degToRad(rotationDeg[0]);
  object.rotation.y += THREE.MathUtils.degToRad(rotationDeg[1]);
  object.rotation.z += THREE.MathUtils.degToRad(rotationDeg[2]);
}

export const RealisticToothDataset: ToothDataset = {
  async buildTooth(ctx: ToothPlacementContext) {
    const { toothId, sourceMesh } = ctx;

    const path = getToothMeshPath(toothId);
    const visualRoot = await loadToothMesh(path);

    // 1) Normalize raw imported asset orientation / mirroring
    applyRealisticToothTransform(visualRoot, toothId);

    // 2) Optional catalog-level correction
    const anchorHint = getToothAnchorHint(toothId);
    applyDefaultRotationDeg(visualRoot, anchorHint.defaultRotationDeg);

    // 3) Scale realistic tooth to roughly match block/source tooth height
    scaleVisualToSourceHeight(visualRoot, sourceMesh);

    // 4) Compute approximate insertion/root anchor after orientation + scale
    const insertionPoint = computePointFromBoxRatio(
      visualRoot,
      anchorHint.insertionPointRatio
    );

    // 5) Stable placement pivot
    const pivot = new THREE.Group();
    pivot.name = `realistic-tooth-pivot-${toothId}`;

    // 6) Place pivot exactly where the source block tooth sits
    pivot.position.copy(sourceMesh.position);
    pivot.quaternion.copy(sourceMesh.quaternion);

    // 7) Offset visible tooth so insertion point lands on pivot origin
    visualRoot.position.sub(insertionPoint);

    // 8) Metadata
    pivot.userData = {
      ...pivot.userData,
      toothId,
      type: "tooth",
      datasetMode: "realistic",
      insertionPointLocal: [insertionPoint.x, insertionPoint.y, insertionPoint.z],
    };

    visualRoot.userData = {
      ...visualRoot.userData,
      toothId,
      type: "tooth",
      datasetMode: "realistic-visual",
    };

    pivot.add(visualRoot);

    return pivot;
  },
};